import {
  areaLevelAtom,
  firstAreaAtom,
  mapRefAtom,
  markersAtom,
  secondAreaAtom,
  showModalAtom,
  showSecondGeocoderAtom,
  transitioningAtom,
} from "@/api/atoms";
import Map from "@/components/Map";
import { UilSearch, UilTimes } from "@iconscout/react-unicons";
import { useMediaQuery } from "@react-hookz/web";
import { useAtom, useAtomValue } from "jotai";

import { countryIdAtom } from "./api/atoms";
import { AreasCard } from "./components/AreasCard";
import { Button } from "./components/Button";
import DraggableMenu from "./components/DraggableMenu";
import { InfoCard } from "./components/InfoCard";
import LayerGeocoder from "./components/LayerGeocoder";
import Modal from "./components/Modal";
import { Navigation } from "./components/Navigation";
import { Spinner } from "./components/Spinner";
import { capitalizeFirstLetter } from "./utils";

function App() {
  const transitioning = useAtomValue(transitioningAtom);
  const [showModal, setShowModal] = useAtom(showModalAtom);

  const isMobileDevice = useMediaQuery("(max-device-width: 480px)");
  const countryId = useAtomValue(countryIdAtom);

  const map = useAtomValue(mapRefAtom);

  const [firstArea, setFirstArea] = useAtom(firstAreaAtom);
  const [secondArea, setSecondArea] = useAtom(secondAreaAtom);
  const areaLevel = useAtomValue(areaLevelAtom);
  const [markers, setMarkers] = useAtom(markersAtom);

  const source =
    areaLevel === "country"
      ? "world-countries"
      : areaLevel === "county"
        ? "eng-districts"
        : areaLevel === "city"
          ? "eng-cities"
          : "eng-boroughs";

  const [_showSecondGeocoder, setShowSecondGeocoder] = useAtom(showSecondGeocoderAtom);

  const handleSubmitEmail = async (email: string, id: number) => {
    if (email.length > 0) {
      const rawResponse = await fetch(`${import.meta.env.VITE_API_URL}/subscribers`, {
        method: "POST",
        headers: {
          Accept: "bikmocation/json",
          "Content-Type": "bikmocation/json",
        },
        body: JSON.stringify({ email, id }),
      });
      const _content = await rawResponse.json();
    }
  };

  const isEngland = firstArea?.name === "United Kingdom";
  const title = `${firstArea ? (isEngland ? "England -" : "Total") : capitalizeFirstLetter(areaLevel)} Bike thefts`;

  const handleCloseModal = () => setShowModal(false);

  const handleRemoveFirstArea = () => {
    console.log("firstArea", firstArea);
    if (!firstArea) return;

    setFirstArea(secondArea);
    setSecondArea(null);

    map?.setFeatureState(
      { source, id: firstArea.id },
      {
        "selected-1": false,
      },
    );

    if (secondArea) {
      if (markers[1]) setMarkers([markers[1]]);

      map?.setFeatureState(
        { source, id: secondArea.id },
        {
          "selected-2": false,
        },
      );
      map?.setFeatureState(
        { source, id: secondArea.id },
        {
          "selected-1": true,
        },
      );
    } else {
      setMarkers([]);
    }
  };

  const handleRemoveSecondArea = () => {
    if (!secondArea) return;

    setSecondArea(null);
    if (markers && markers.length > 1) setMarkers([markers[0]]);

    setShowSecondGeocoder(false);

    map?.setFeatureState(
      { source, id: secondArea.id },
      {
        "selected-2": false,
      },
    );
  };

  return (
    <main className="relative h-screen w-screen bg-gray-600">
      <Navigation />
      <Map />
      {transitioning && (
        <div className="absolute inset-0 z-50 grid h-screen w-screen place-content-center bg-gray-600 bg-opacity-80 text-white">
          <Spinner />
        </div>
      )}
      {!isMobileDevice && (
        <section className="absolute left-5 max-h-[70vh] overflow-hidden overflow-y-auto md:top-44">
          <InfoCard title={title} />
        </section>
      )}
      {showModal && countryId && (
        <Modal onSubmit={handleSubmitEmail} onClose={handleCloseModal} countryId={countryId} />
      )}
      <div className="fixed bottom-0 left-0 z-10 flex h-fit w-screen flex-col gap-2 bg-gray-600 bg-opacity-80 p-4 md:left-5 md:top-20 md:w-[420px] md:bg-transparent md:p-0">
        {firstArea && (
          <Button intent="secondary" size="small" onClick={handleRemoveFirstArea}>
            <div className="mr-auto flex items-center gap-4 text-left text-xs">
              <UilSearch size={20} />
              {firstArea.name}
            </div>
            <span className="rounded-full bg-gray-600 p-1 text-white">
              <UilTimes size={16} />
            </span>
          </Button>
        )}
        {!firstArea && <LayerGeocoder placeholder="Search or select an area" isFirst />}
        {firstArea && !secondArea && (
          <LayerGeocoder placeholder="Search or select an area to compare to" />
        )}
        {secondArea && (
          <Button
            intent="secondary"
            size="small"
            className="justify-between"
            onClick={handleRemoveSecondArea}
          >
            <div className="mr-auto flex items-center gap-4 text-left text-xs">
              <UilSearch size={20} />
              {secondArea.name}
            </div>
            <span className="rounded-full bg-gray-600 p-1 text-white">
              <UilTimes size={16} />
            </span>
          </Button>
        )}
        {!firstArea && (
          <Button
            size="small"
            className="!justify-start text-xs"
            intent="tertiary"
            onClick={() => setShowSecondGeocoder(true)}
            disabled={markers.length === 0}
          >
            <UilSearch size={20} />+ Add another address to compare
          </Button>
        )}
      </div>
      {isMobileDevice && <DraggableMenu />}
      {isMobileDevice && (
        <div
          id="geocoder-container"
          className="absolute bottom-0 left-0 w-full bg-gray-600 bg-opacity-80"
        />
      )}
      <div className="invisible fixed right-0 top-20 bg-gray-600 bg-opacity-50 px-5 md:visible">
        <AreasCard />
      </div>
    </main>
  );
}

export default App;
